import * as React from "react";

import { bigintReplacer } from "@/shared/json";

import {
  ViewingContext,
  ViewingContextMap,
  ViewingContextSourceName,
} from "./ViewingContext";
import { ViewingContextService } from "./ViewingContextService";

export const useRegisterViewingContext = <T extends ViewingContextSourceName>(
  name: T,
  value: Partial<ViewingContextMap[T]>,
) => {
  // Generate a stable ID for this component instance
  const componentId = React.useMemo(() => Symbol(name), [name]);

  // Deep compare the value to avoid unnecessary updates. `bigintReplacer` is
  // required, not defensive: registered values include domain records parsed
  // with `parseTimestampsAsBigInt`, so any ns-epoch field (start_time,
  // min_timestamp_ns, ...) arrives as a `bigint` that plain stringify throws
  // on — which would tear the page down through the error boundary.
  const valueKey = JSON.stringify(value, bigintReplacer);

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const memoizedValue = React.useMemo(() => value, [valueKey]);

  React.useEffect(
    function addContext() {
      const service = ViewingContextService.getInstance();
      service.addViewingContextPart(componentId, name, memoizedValue);

      return function removeContext() {
        service.removeViewingContextPart(componentId);
      };
    },
    [componentId, name, memoizedValue],
  );
};

export const useViewingContext = (): ViewingContext => {
  const service = ViewingContextService.getInstance();

  return React.useSyncExternalStore(
    service.subscribe.bind(service),
    service.getViewingContext.bind(service),
  );
};

/**
 * The org that owns whatever resource is currently being viewed, or `null`
 * when no single owning org can be resolved (nothing registered, record not
 * yet loaded, or a view spanning more than one org).
 *
 * See {@link ViewingContextService.getViewingOrgId} for the resolution rule.
 */
export const useViewingResourceOrg = (): string | null => {
  const service = ViewingContextService.getInstance();

  return React.useSyncExternalStore(
    service.subscribe.bind(service),
    service.getViewingOrgId.bind(service),
  );
};
